// src/pages/AdminPuzzles.js
import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, getDocs, doc, updateDoc,
  deleteDoc, query, orderBy, onSnapshot, where
} from "firebase/firestore";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import "./AdminPuzzles.css";

const STARTING_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const PIECES = [
  { code: "wK", sym: "♔" }, { code: "wQ", sym: "♕" }, { code: "wR", sym: "♖" },
  { code: "wB", sym: "♗" }, { code: "wN", sym: "♘" }, { code: "wP", sym: "♙" },
  { code: "bK", sym: "♚" }, { code: "bQ", sym: "♛" }, { code: "bR", sym: "♜" },
  { code: "bB", sym: "♝" }, { code: "bN", sym: "♞" }, { code: "bP", sym: "♟" },
];



export default function AdminPuzzles({ students }) {
  const [tab, setTab] = useState("sets"); // sets | create
  const [puzzleSets, setPuzzleSets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create flow
  const [setName, setSetName] = useState("");
  const [puzzles, setPuzzles] = useState([]); // array of puzzle objects
  const [editingPuzzle, setEditingPuzzle] = useState(null); // puzzle being built
  const [showPuzzleEditor, setShowPuzzleEditor] = useState(false);
  const [assignStudents, setAssignStudents] = useState([]);
  const [showPublish, setShowPublish] = useState(false);
  const [publishMsg, setPublishMsg] = useState("");

  // Puzzle editor state
  const [editorFen, setEditorFen] = useState(STARTING_FEN);
  const [editorColorToMove, setEditorColorToMove] = useState("w");
  const [editorMode, setEditorMode] = useState("pgn"); // pgn | board
  const [editorPgn, setEditorPgn] = useState("");
  const [editorPgnMsg, setEditorPgnMsg] = useState("");
  const [editorFenInput, setEditorFenInput] = useState("");
  const [selectedEditPiece, setSelectedEditPiece] = useState(null);
  const [highlightSquares, setHighlightSquares] = useState({});
  const [solutionMoves, setSolutionMoves] = useState([]); // [{from,to,san}]
  const [recordingMoves, setRecordingMoves] = useState(false);
  const [solutionGame, setSolutionGame] = useState(null);
  const [solutionFen, setSolutionFen] = useState("");
  const [selectedSolutionSquare, setSelectedSolutionSquare] = useState(null);

  // View set
  const [viewSet, setViewSet] = useState(null);
  // Per-student progress for the currently-viewed set, loaded from the
  // puzzleProgress collection (students save there now, not into puzzleSets).
  const [viewSetProgress, setViewSetProgress] = useState({}); // { studentUid: attemptsMap }
  const [viewProgressLoading, setViewProgressLoading] = useState(false);

  // Library tab (browse the imported Lichess puzzleLibrary and assign to students)
  const [libTopics, setLibTopics] = useState(null);        // null = not loaded yet
  const [libLoading, setLibLoading] = useState(false);
  const [libSearch, setLibSearch] = useState("");
  const [libColor, setLibColor] = useState("all");         // all | white | black
  const [libAssign, setLibAssign] = useState(null);        // topic being assigned
  const [libAssignStudents, setLibAssignStudents] = useState([]);
  const [libMsg, setLibMsg] = useState("");

  // When the teacher opens a set's results, load every student's progress
  // for it from the puzzleProgress collection.
  useEffect(() => {
    if (!viewSet) { setViewSetProgress({}); return; }
    let cancelled = false;
    setViewProgressLoading(true);
    getDocs(query(collection(db, "puzzleProgress"), where("setId", "==", viewSet.id)))
      .then(snap => {
        if (cancelled) return;
        const map = {};
        snap.forEach(d => {
          const data = d.data();
          if (data.studentId) map[data.studentId] = data.attempts || {};
        });
        setViewSetProgress(map);
        setViewProgressLoading(false);
      })
      .catch(err => {
        console.error("Could not load student progress:", err);
        if (!cancelled) { setViewSetProgress({}); setViewProgressLoading(false); }
      });
    return () => { cancelled = true; };
  }, [viewSet]);

  // Load the puzzle library the first time the Library tab is opened.
  useEffect(() => {
    if (tab !== "library" || libTopics !== null) return;
    setLibLoading(true);
    getDocs(collection(db, "puzzleLibrary"))
      .then(snap => {
        const rows = [];
        snap.forEach(d => {
          const data = d.data();
          rows.push({
            docId: d.id,
            name: data.name || d.id,
            color: (data.color || "").toLowerCase(),
            puzzles: Array.isArray(data.puzzles) ? data.puzzles : [],
          });
        });
        rows.sort((a, b) => a.name.localeCompare(b.name) || a.color.localeCompare(b.color));
        setLibTopics(rows);
        setLibLoading(false);
      })
      .catch(err => {
        console.error("Could not load puzzle library:", err);
        setLibTopics([]);
        setLibLoading(false);
      });
  }, [tab, libTopics]);
  const [viewStudent, setViewStudent] = useState(null);
  const [reviewSelPuzzle, setReviewSelPuzzle] = useState(0);
  const [reviewNavIdx, setReviewNavIdx] = useState(0);

  // Reset review state when student changes
  useEffect(() => {
    setReviewSelPuzzle(0);
    setReviewNavIdx(0);
  }, [viewStudent]);

  useEffect(() => {
    setReviewNavIdx(0);
  }, [reviewSelPuzzle]);

  useEffect(() => {
    const q = query(collection(db, "puzzleSets"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      const sets = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPuzzleSets(sets);
      setLoading(false);
      // Keep viewSet in sync with latest data
      setViewSet(prev => {
        if (!prev) return null;
        return sets.find(s => s.id === prev.id) || prev;
      });
    });
    return () => unsub();
  }, []);

  // When the teacher opens a set's results, load every student's progress for it.
  useEffect(() => {
    if (!viewSet) { setViewSetProgress({}); return; }
    let cancelled = false;
    setViewProgressLoading(true);
    getDocs(query(collection(db, "puzzleProgress"), where("setId", "==", viewSet.id)))
      .then(snap => {
        if (cancelled) return;
        const map = {};
        snap.forEach(d => {
          const data = d.data();
          if (data.studentId) map[data.studentId] = data.attempts || {};
        });
        setViewSetProgress(map);
        setViewProgressLoading(false);
      })
      .catch(err => {
        console.error("Could not load student progress:", err);
        if (!cancelled) { setViewSetProgress({}); setViewProgressLoading(false); }
      });
    return () => { cancelled = true; };
  }, [viewSet]);

  // ── Puzzle editor ──────────────────────────────────────────────
  function openNewPuzzle() {
    setEditorFen(STARTING_FEN);
    setEditorColorToMove("w");
    setEditorMode("pgn");
    setEditorPgn("");
    setEditorPgnMsg("");
    setEditorFenInput("");
    setSelectedEditPiece(null);
    setSolutionMoves([]);
    setRecordingMoves(false);
    setSolutionGame(null);
    setSolutionFen("");
    setEditingPuzzle(null);
    setShowPuzzleEditor(true);
  }

  function loadEditorPgn() {
    try {
      const g = new Chess();
      g.loadPgn(editorPgn.trim());
      setEditorFen(g.fen());
      setEditorPgnMsg("✓ Loaded!");
      setTimeout(() => setEditorPgnMsg(""), 2000);
    } catch { setEditorPgnMsg("✗ Invalid PGN"); }
  }

  function loadEditorFen() {
    try {
      const g = new Chess();
      g.load(editorFenInput.trim());
      setEditorFen(g.fen());
    } catch { alert("Invalid FEN"); }
  }

  function onEditorPieceClick(piece, square) {
    if (recordingMoves) {
      // Show legal moves during solution recording
      try {
        const g = new Chess(solutionFen || editorFen);
        const moves = g.moves({ square, verbose: true });
        const highlights = {};
        moves.forEach(m => {
          highlights[m.to] = {
            background: g.get(m.to)
              ? "radial-gradient(circle, rgba(200,80,80,0.5) 70%, transparent 70%)"
              : "radial-gradient(circle, rgba(200,169,110,0.4) 30%, transparent 30%)",
            borderRadius: "50%"
          };
        });
        highlights[square] = { background: "rgba(200,169,110,0.25)", borderRadius: "4px" };
        setHighlightSquares(highlights);
      } catch { setHighlightSquares({}); }
    }
  }

  function getPieceCharAt(currentFen, square) {
    try {
      const rows = currentFen.split(" ")[0].split("/");
      const col = square.charCodeAt(0) - 97;
      const row = 8 - parseInt(square[1]);
      let c = 0;
      for (const ch of rows[row]) {
        if (isNaN(ch)) { if (c === col) return ch; c++; }
        else { c += parseInt(ch); }
      }
    } catch {}
    return null;
  }

  function onEditorDrop(src, tgt, piece) {
    if (recordingMoves) {
      try {
        const g = new Chess(solutionFen || editorFen);
        const move = g.move({ from: src, to: tgt, promotion: "q" });
        if (!move) return false;
        setSolutionMoves(prev => [...prev, { from: src, to: tgt, san: move.san }]);
        setSolutionFen(g.fen());
        setSolutionGame(g);
        setHighlightSquares({});
        return true;
      } catch { return false; }
    }
    // Free board edit — use direct FEN manipulation for any board state
    try {
      if (selectedEditPiece) return false;
      const pieceChar = getPieceCharAt(editorFen, src);
      if (!pieceChar) return false;
      const color = pieceChar === pieceChar.toUpperCase() ? "w" : "b";
      const type = pieceChar.toUpperCase();
      const pieceCode = color + type;
      const newFen = editSquareDirect(editSquareDirect(editorFen, src, "erase"), tgt, pieceCode);
      setEditorFen(newFen);
      return true;
    } catch(e) { console.log("Editor drop error:", e); return false; }
  }

  function onEditorSquareClick(square) {
    // During solution recording — handle click-to-move
    if (recordingMoves) {
      if (selectedSolutionSquare) {
        // Try to move from selected square to clicked square
        try {
          const g = new Chess(solutionFen || editorFen);
          const move = g.move({ from: selectedSolutionSquare, to: square, promotion: "q" });
          if (move) {
            setSolutionMoves(prev => [...prev, { from: selectedSolutionSquare, to: square, san: move.san }]);
            setSolutionFen(g.fen());
            setSolutionGame(g);
          }
        } catch {}
        setSelectedSolutionSquare(null);
        setHighlightSquares({});
        return;
      }
      // Select this square and show legal moves
      try {
        const g = new Chess(solutionFen || editorFen);
        const moves = g.moves({ square, verbose: true });
        if (moves.length > 0) {
          const highlights = {};
          moves.forEach(m => {
            highlights[m.to] = {
              background: g.get(m.to)
                ? "radial-gradient(circle, transparent 58%, rgba(200,169,110,0.8) 58%, rgba(200,169,110,0.8) 72%, transparent 72%)"
                : "radial-gradient(circle, rgba(200,169,110,0.75) 26%, transparent 26%)",
              borderRadius: "50%"
            };
          });
          highlights[square] = { background: "rgba(200,169,110,0.35)", borderRadius: "4px" };
          setHighlightSquares(highlights);
          setSelectedSolutionSquare(square);
        }
      } catch {}
      return;
    }
    // Board edit mode — place piece
    if (!selectedEditPiece) return;
    try {
      const newFen = editSquareDirect(editorFen, square, selectedEditPiece);
      setEditorFen(newFen);
    } catch(e) { console.log("Editor square click error:", e); }
  }

  function editSquareDirect(currentFen, square, pieceCode) {
    const fenParts = currentFen.split(" ");
    const rows = fenParts[0].split("/");
    const board = rows.map(row => {
      const cells = [];
      for (const ch of row) {
        if (isNaN(ch)) { cells.push(ch); }
        else { for (let i = 0; i < parseInt(ch); i++) cells.push(null); }
      }
      return cells;
    });
    const col = square.charCodeAt(0) - 97;
    const row = 8 - parseInt(square[1]);
    if (pieceCode === "erase") {
      board[row][col] = null;
    } else {
      const color = pieceCode[0];
      const type = pieceCode[1];
      board[row][col] = color === "w" ? type.toUpperCase() : type.toLowerCase();
    }
    const newBoardFen = board.map(row => {
      let fenRow = ""; let empty = 0;
      for (const cell of row) {
        if (cell === null) { empty++; }
        else { if (empty > 0) { fenRow += empty; empty = 0; } fenRow += cell; }
      }
      if (empty > 0) fenRow += empty;
      return fenRow;
    }).join("/");
    fenParts[0] = newBoardFen;
    return fenParts.join(" ");
  }

  function startRecording() {
    setSolutionMoves([]);
    setSolutionFen(editorFen);
    setSolutionGame(new Chess(editorFen));
    setRecordingMoves(true);
    setSelectedEditPiece(null);
    setSelectedSolutionSquare(null);
    setHighlightSquares({});
  }

  function undoSolutionMove() {
    if (solutionMoves.length === 0) return;
    const newMoves = solutionMoves.slice(0, -1);
    setSolutionMoves(newMoves);
    const g = new Chess(editorFen);
    newMoves.forEach(m => { try { g.move({ from: m.from, to: m.to, promotion: "q" }); } catch {} });
    setSolutionFen(g.fen());
  }

  function savePuzzle() {
    if (solutionMoves.length === 0) { alert("Please record at least one solution move."); return; }

    // Detect if the first recorded move is by the opponent (pre-move)
    // We do this by playing the first move on a Chess instance and checking which color moved
    let preMoves = [];
    let playerMoves = [...solutionMoves];
    try {
      const g = new Chess(editorFen);
      const firstMoveSan = solutionMoves[0]?.san;
      if (firstMoveSan) {
        // Check what color is to move in the starting FEN
        const fenColor = editorFen.split(" ")[1]; // "w" or "b"
        // If fenColor doesn't match colorToMove, the board FEN is already set up
        // Just check if first solution move is by the opponent of colorToMove
        const testG = new Chess(editorFen);
        const testMove = testG.move({ from: solutionMoves[0].from, to: solutionMoves[0].to, promotion: "q" });
        if (testMove) {
          const firstMoveColor = testMove.color; // who actually moved
          if (firstMoveColor !== editorColorToMove) {
            // First move is opponent's pre-move
            // Split: preMoves = all leading opponent moves, rest = player+opponent alternating
            preMoves = [];
            let i = 0;
            while (i < solutionMoves.length) {
              const g2 = new Chess(editorFen);
              // replay up to move i to find its color
              for (let j = 0; j < i; j++) {
                try { g2.move({ from: solutionMoves[j].from, to: solutionMoves[j].to, promotion: "q" }); } catch {}
              }
              const moveColor = g2.turn(); // color about to move = color of move i
              if (moveColor !== editorColorToMove && i < 1) {
                // Only the very first move can be a pre-move for now
                preMoves.push(solutionMoves[i]);
                i++;
              } else {
                break;
              }
            }
            playerMoves = solutionMoves.slice(preMoves.length);
          }
        }
      }
    } catch(e) { console.log("Pre-move detection error:", e); }

    const puzzle = {
      fen: editorFen,
      colorToMove: editorColorToMove,
      solution: playerMoves,
      preMoves: preMoves, // opponent moves that play automatically before student
      id: Date.now().toString(),
    };
    if (editingPuzzle !== null) {
      setPuzzles(prev => prev.map((p, i) => i === editingPuzzle ? puzzle : p));
    } else {
      setPuzzles(prev => [...prev, puzzle]);
    }
    setShowPuzzleEditor(false);
  }

  function removePuzzle(idx) {
    setPuzzles(prev => prev.filter((_, i) => i !== idx));
  }

  function editPuzzle(idx) {
    const p = puzzles[idx];
    setEditorFen(p.fen);
    setEditorColorToMove(p.colorToMove);
    setSolutionMoves(p.solution);
    setRecordingMoves(false);
    setSolutionFen(p.fen);
    setEditingPuzzle(idx);
    setShowPuzzleEditor(true);
  }

  // ── Publish ────────────────────────────────────────────────────
  async function publishSet() {
    if (!setName.trim()) { alert("Please enter a name for this puzzle set."); return; }
    if (puzzles.length === 0) { alert("Please add at least one puzzle."); return; }
    if (assignStudents.length === 0) { alert("Please assign to at least one student."); return; }
    setPublishMsg("Publishing...");
    try {
      await addDoc(collection(db, "puzzleSets"), {
        name: setName.trim(),
        puzzles,
        studentIds: assignStudents,
        createdAt: new Date().toISOString(),
        totalPuzzles: puzzles.length,
      });
      setPublishMsg("✓ Published!");
      setTimeout(() => {
        setShowPublish(false);
        setPublishMsg("");
        setPuzzles([]);
        setSetName("");
        setAssignStudents([]);
        setTab("sets");
      }, 1500);
    } catch (e) {
      setPublishMsg("Error: " + e.message);
    }
  }

  async function deleteSet(id) {
    if (!window.confirm("Delete this puzzle set?")) return;
    await deleteDoc(doc(db, "puzzleSets", id));
  }

  function getStudentName(id) {
    return students.find(s => s.id === id)?.name || id;
  }

  // Publish a library topic as a puzzle set assigned to selected students.
  // Writes the exact same document shape as publishSet above.
  async function publishLibrarySet() {
    if (!libAssign) return;
    if (libAssignStudents.length === 0) { setLibMsg("Select at least one student."); return; }
    setLibMsg("Publishing...");
    try {
      const colorTag = libAssign.color ? ` (${libAssign.color})` : "";
      await addDoc(collection(db, "puzzleSets"), {
        name: `${libAssign.name}${colorTag}`,
        puzzles: libAssign.puzzles,
        studentIds: libAssignStudents,
        createdAt: new Date().toISOString(),
        totalPuzzles: libAssign.puzzles.length,
      });
      setLibMsg("✓ Published!");
      setTimeout(() => {
        setLibAssign(null);
        setLibAssignStudents([]);
        setLibMsg("");
        setTab("sets");
      }, 900);
    } catch (err) {
      console.error("Library publish failed:", err);
      setLibMsg("Failed to publish — please try again.");
    }
  }

  function getSetProgress(set) {
    if (!set.attempts) return null;
    return set.attempts;
  }

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="puzzles-admin">
      <div className="puzzles-header">
        <div>
          <h2>Puzzles</h2>
          <p className="page-sub">Create and manage puzzle sets for your students</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className={`btn ${tab === "sets" ? "btn-primary" : "btn-secondary"}`} onClick={() => setTab("sets")}>My Sets</button>
          <button className={`btn ${tab === "create" ? "btn-primary" : "btn-secondary"}`} onClick={() => setTab("create")}>+ Create Set</button>
          <button className={`btn ${tab === "library" ? "btn-primary" : "btn-secondary"}`} onClick={() => setTab("library")}>📚 Library</button>
        </div>
      </div>

      {/* ── Sets tab ── */}
      <div style={{ display: tab === "sets" ? "block" : "none" }}>
      {true && (
        <div className="puzzle-sets-list">
          {loading && <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>}
          {!loading && puzzleSets.length === 0 && (
            <div className="empty-state"><div>🧩</div><p>No puzzle sets yet</p><button className="btn btn-primary" onClick={() => setTab("create")}>Create your first set</button></div>
          )}
          {puzzleSets.map(set => (
            <div key={set.id} className="puzzle-set-card">
              <div className="pset-left">
                <h3>{set.name}</h3>
                <p className="pset-meta">{set.totalPuzzles} puzzles · Assigned to: {set.studentIds?.map(id => getStudentName(id)).join(", ")}</p>
                <p className="pset-date">{new Date(set.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
              </div>
              <div className="pset-right">
                <button className="btn btn-secondary" onClick={() => setViewSet(set)}>View Results</button>
                <button className="btn btn-secondary" style={{ color: "var(--danger)" }} onClick={() => deleteSet(set.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* ── Create tab ── */}
      <div style={{ display: tab === "create" ? "block" : "none" }}>
      {true && (
        <div className="create-set">
          <div className="create-set-top">
            <div style={{ flex: 1 }}>
              <label>Set Name</label>
              <input value={setName} onChange={e => setSetName(e.target.value)} placeholder="e.g. Checkmate in 2" style={{ maxWidth: 340 }} />
            </div>
            <button className="btn btn-primary" onClick={openNewPuzzle}>+ Add Puzzle</button>
          </div>

          {puzzles.length === 0 && (
            <div className="empty-state" style={{ padding: "40px 20px" }}>
              <div>♟</div><p>No puzzles added yet. Click "Add Puzzle" to start.</p>
            </div>
          )}

          <div className="puzzles-preview-list">
            {puzzles.map((p, i) => (
              <div key={i} className="puzzle-preview-card">
                <div className="puzzle-preview-board">
                  <Chessboard position={p.fen} arePiecesDraggable={false}
                    boardOrientation={p.colorToMove === "w" ? "white" : "black"}
                    customBoardStyle={{ borderRadius: 6 }}
                    customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
                    customDarkSquareStyle={{ backgroundColor: "#b58863" }}
                  />
                </div>
                <div className="puzzle-preview-info">
                  <div className="puzzle-num">Puzzle {i + 1}</div>
                  <div className="puzzle-color">{p.colorToMove === "w" ? "♔ White to move" : "♚ Black to move"}</div>
                  <div className="puzzle-solution">Solution: {[...(p.preMoves||[]), ...p.solution].map(m => m.san).join(", ")}{p.preMoves?.length > 0 ? <span style={{fontSize:11,color:"var(--text-muted)",marginLeft:6}}>(first {p.preMoves.length} auto)</span> : null}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => editPuzzle(i)}>Edit</button>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: "6px 12px", color: "var(--danger)" }} onClick={() => removePuzzle(i)}>Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {puzzles.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <button className="btn btn-primary" style={{ padding: "12px 32px", fontSize: 15 }} onClick={() => setShowPublish(true)}>
                Publish Set ({puzzles.length} puzzle{puzzles.length !== 1 ? "s" : ""})
              </button>
            </div>
          )}
        </div>
      )}

      )}
      </div>

      {/* ── Puzzle Editor Modal ── */}
      {showPuzzleEditor && (
        <div className="modal-overlay" onClick={() => setShowPuzzleEditor(false)}>
          <div className="puzzle-editor-modal" onClick={e => e.stopPropagation()}>
            <div className="editor-left">
              <h3 style={{ marginBottom: 12 }}>{editingPuzzle !== null ? "Edit Puzzle" : "New Puzzle"}</h3>

              {/* Mode tabs */}
              <div className="editor-mode-tabs">
                <button className={`editor-tab ${editorMode === "pgn" ? "active" : ""}`} onClick={() => setEditorMode("pgn")}>Load PGN</button>
                <button className={`editor-tab ${editorMode === "fen" ? "active" : ""}`} onClick={() => setEditorMode("fen")}>Load FEN</button>
                <button className={`editor-tab ${editorMode === "board" ? "active" : ""}`} onClick={() => setEditorMode("board")}>Edit Board</button>
              </div>

              {editorMode === "pgn" && (
                <div className="editor-input-section">
                  <label>Paste PGN</label>
                  <textarea value={editorPgn} onChange={e => setEditorPgn(e.target.value)} rows={5} placeholder="1. e4 e5 2. Nf3..." style={{ fontFamily: "monospace", fontSize: 12 }} />
                  {editorPgnMsg && <p style={{ color: editorPgnMsg.startsWith("✓") ? "var(--success)" : "var(--danger)", fontSize: 13 }}>{editorPgnMsg}</p>}
                  <button className="btn btn-secondary" style={{ width: "100%", marginTop: 8 }} onClick={loadEditorPgn}>Load PGN</button>
                </div>
              )}

              {editorMode === "fen" && (
                <div className="editor-input-section">
                  <label>Paste FEN</label>
                  <input value={editorFenInput} onChange={e => setEditorFenInput(e.target.value)} placeholder="rnbqkbnr/..." />
                  <button className="btn btn-secondary" style={{ width: "100%", marginTop: 8 }} onClick={loadEditorFen}>Load FEN</button>
                </div>
              )}

              {editorMode === "board" && (
                <div className="editor-input-section">
                  <label>Select piece to place</label>
                  <div className="piece-palette" style={{ gridTemplateColumns: "repeat(6,1fr)" }}>
                    {PIECES.map(p => (
                      <button key={p.code}
                        className={`palette-piece ${selectedEditPiece === p.code ? "selected" : ""}`}
                        onClick={() => setSelectedEditPiece(selectedEditPiece === p.code ? null : p.code)}
                      >{p.sym}</button>
                    ))}
                  </div>
                  <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>Select a piece then click a square. Drag pieces to move them.</p>
                </div>
              )}

              <div className="editor-color-section">
                <label>Color to Move</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className={`perm-btn ${editorColorToMove === "w" ? "active" : ""}`} onClick={() => setEditorColorToMove("w")}>♔ White</button>
                  <button className={`perm-btn ${editorColorToMove === "b" ? "active" : ""}`} onClick={() => setEditorColorToMove("b")}>♚ Black</button>
                </div>
              </div>

              <div className="editor-solution-section">
                <label>Solution Moves</label>
                {!recordingMoves ? (
                  <button className="btn btn-primary" style={{ width: "100%" }} onClick={startRecording}>▶ Record Solution Moves</button>
                ) : (
                  <div>
                    <p style={{ fontSize: 12, color: "var(--accent)", marginBottom: 8 }}>▶ Recording — play the solution on the board</p>
                    <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={undoSolutionMove}>↩ Undo</button>
                    <button className="btn btn-secondary" style={{ fontSize: 12, marginLeft: 6 }} onClick={() => { setRecordingMoves(false); setSolutionMoves([]); setSolutionFen(editorFen); }}>Clear</button>
                  </div>
                )}
                {solutionMoves.length > 0 && (
                  <div className="solution-moves">
                    {solutionMoves.map((m, i) => <span key={i} className="solution-move">{i + 1}. {m.san}</span>)}
                  </div>
                )}
              </div>
            </div>

            <div className="editor-right">
              <div style={{ width: "100%", maxWidth: 400 }}>
                <Chessboard
                  position={recordingMoves ? (solutionFen || editorFen) : editorFen}
                  onPieceDrop={onEditorDrop}
                  onSquareClick={onEditorSquareClick}
                  onPieceClick={(piece, square) => {
                    if (recordingMoves) onEditorSquareClick(square);
                  }}
                  boardOrientation={editorColorToMove === "w" ? "white" : "black"}
                  arePiecesDraggable={true}
                  customSquareStyles={highlightSquares}
                  customBoardStyle={{ borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
                  customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
                  customDarkSquareStyle={{ backgroundColor: "#b58863" }}
                />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <button className="btn btn-secondary" onClick={() => { setEditorFen("8/8/8/8/8/8/8/8 w - - 0 1"); setHighlightSquares({}); setSolutionMoves([]); setSolutionFen("8/8/8/8/8/8/8/8 w - - 0 1"); setRecordingMoves(false); }} title="Clear all pieces from board">🗑️ Clear Board</button>
                <button className="btn btn-secondary" onClick={() => setShowPuzzleEditor(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={savePuzzle}>Save Puzzle</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Publish Modal ── */}
      {/* ── Library tab ── */}
      <div style={{ display: tab === "library" ? "block" : "none" }}>
        <div className="library-bar">
          <div className="lib-search">
            <span className="lib-search-icon">🔎</span>
            <input className="lib-search-input" placeholder="Search topics…" value={libSearch} onChange={e => setLibSearch(e.target.value)} />
            {libSearch && <button className="lib-search-clear" onClick={() => setLibSearch("")}>×</button>}
          </div>
          <div className="library-bar-controls">
            <div className="lib-color-filter">
              {["all", "white", "black"].map(c => (
                <button key={c} className={`btn ${libColor === c ? "btn-primary" : "btn-secondary"}`} onClick={() => setLibColor(c)}>
                  {c === "all" ? "All" : c === "white" ? "⚪ White" : "⚫ Black"}
                </button>
              ))}
            </div>
          </div>
        </div>
        {libLoading && <p style={{ color: "var(--text-muted)" }}>Loading library…</p>}
        {!libLoading && libTopics && libTopics.length === 0 && (
          <div className="empty-state"><div>📚</div><p>No library puzzles found</p></div>
        )}
        <div className="library-grid">
          {(libTopics || [])
            .filter(tp => libColor === "all" || tp.color === libColor)
            .filter(tp => tp.name.toLowerCase().includes(libSearch.toLowerCase()))
            .map(tp => (
              <div key={tp.docId} className="library-card" onClick={() => { setLibAssign(tp); setLibAssignStudents([]); setLibMsg(""); }}>
                <div className="lib-card-top">
                  <span className={`lib-color-dot ${tp.color}`}></span>
                  <span className="lib-card-count">{tp.puzzles.length} puzzles</span>
                </div>
                <div className="lib-card-name">{tp.name}</div>
                <div className="lib-card-sub">{tp.color ? `${tp.color} to move` : "mixed"} · click to assign</div>
              </div>
            ))}
        </div>
      </div>

      {/* ── Library assign modal ── */}
      {libAssign && (
        <div className="modal-overlay" onClick={() => setLibAssign(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Assign Library Set</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 20px" }}>
              "{libAssign.name}" — {libAssign.puzzles.length} puzzles{libAssign.color ? ` · ${libAssign.color} to move` : ""}
            </p>
            <label>Assign to Students</label>
            <div className="student-checkboxes" style={{ marginBottom: 20 }}>
              {students.map(s => (
                <label key={s.id} className="checkbox-label">
                  <input type="checkbox"
                    checked={libAssignStudents.includes(s.id)}
                    onChange={() => setLibAssignStudents(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id])}
                  />
                  {s.name}
                </label>
              ))}
              {students.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No students found.</p>}
            </div>
            {libMsg && <p style={{ color: libMsg.startsWith("✓") ? "var(--success)" : libMsg === "Publishing..." ? "var(--accent)" : "var(--danger)", fontSize: 13, marginBottom: 10 }}>{libMsg}</p>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setLibAssign(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={publishLibrarySet}>Publish to Students</button>
            </div>
          </div>
        </div>
      )}

      {showPublish && (
        <div className="modal-overlay" onClick={() => setShowPublish(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Publish Puzzle Set</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 20px" }}>"{setName}" — {puzzles.length} puzzles</p>
            <label>Assign to Students</label>
            <div className="student-checkboxes" style={{ marginBottom: 20 }}>
              {students.map(s => (
                <label key={s.id} className="checkbox-label">
                  <input type="checkbox"
                    checked={assignStudents.includes(s.id)}
                    onChange={() => setAssignStudents(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id])}
                  />
                  {s.name}
                </label>
              ))}
              {students.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No students found.</p>}
            </div>
            {publishMsg && <p style={{ color: publishMsg.startsWith("✓") ? "var(--success)" : publishMsg === "Publishing..." ? "var(--accent)" : "var(--danger)", fontSize: 13, marginBottom: 10 }}>{publishMsg}</p>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowPublish(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={publishSet}>Publish</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Set Results Modal ── */}
      {viewSet && (
        <div className="modal-overlay" onClick={() => { setViewSet(null); setViewStudent(null); }}>
          <div className="results-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3>{viewSet.name} — Results</h3>
              <button className="btn btn-secondary" onClick={() => { setViewSet(null); setViewStudent(null); }}>Close</button>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
              {viewSet.totalPuzzles} puzzles
              {!viewProgressLoading && viewSet.studentIds?.length > 0 && (() => {
                const started = viewSet.studentIds.filter(sid => viewSetProgress[sid] && Object.keys(viewSetProgress[sid]).length > 0).length;
                return ` · ${started} of ${viewSet.studentIds.length} student${viewSet.studentIds.length !== 1 ? "s" : ""} started`;
              })()}
            </p>

            {viewProgressLoading && <p style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading student progress…</p>}
            {!viewProgressLoading && viewSet.studentIds?.length === 0 && (
              <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No students assigned to this set yet.</p>
            )}

            {viewSet.studentIds?.map(sid => {
              const attempts = viewSetProgress[sid];
              const sName = getStudentName(sid);
              const attempted = attempts ? Object.keys(attempts).length : 0;
              const correct = attempts ? Object.values(attempts).filter(a => a.correct).length : 0;
              return (
                <div key={sid} className="student-result-row" onClick={() => setViewStudent(viewStudent === sid ? null : sid)}>
                  <div className="student-result-info">
                    <div className="student-avatar" style={{ width: 36, height: 36, fontSize: 15 }}>{sName.charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{sName}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{attempted}/{viewSet.totalPuzzles} attempted · {correct} correct</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--accent)" }}>{viewStudent === sid ? "▲ Hide" : "▼ Details"}</div>

                  {viewStudent === sid && attempts && (() => {
                    const puzzle = viewSet.puzzles[reviewSelPuzzle];
                    const attempt = attempts[reviewSelPuzzle];
                    // Build FEN sequence
                    const fens = [puzzle.fen];
                    const g2 = new Chess(puzzle.fen);
                    (attempt?.moves || []).forEach(m => {
                      try { g2.move({ from: m.from, to: m.to, promotion: "q" }); fens.push(g2.fen()); } catch {}
                    });
                    const displayFen = fens[reviewNavIdx] || puzzle.fen;
                    return (
                      <div className="admin-board-review">
                        <div className="admin-board-puzzles">
                          {viewSet.puzzles.map((p, i) => {
                            const att = attempts[i];
                            return (
                              <button key={i}
                                className={`puzzle-select-btn ${reviewSelPuzzle === i ? "active" : ""} ${!att ? "unattempted" : att.correct ? "correct" : "wrong"}`}
                                onClick={(e) => { e.stopPropagation(); setReviewSelPuzzle(i); }}>
                                #{i+1} {!att ? "—" : att.correct ? "✓" : "✗"}
                              </button>
                            );
                          })}
                        </div>
                        <div className="admin-board-view">
                          <div style={{ width: 220 }}>
                            <Chessboard
                              position={displayFen}
                              arePiecesDraggable={false}
                              boardOrientation={puzzle.colorToMove === "w" ? "white" : "black"}
                              customBoardStyle={{ borderRadius: 6 }}
                              customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
                              customDarkSquareStyle={{ backgroundColor: "#b58863" }}
                            />
                          </div>
                          <div className="admin-board-info">
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
                              {puzzle.colorToMove === "w" ? "♔ White to move" : "♚ Black to move"}
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label>Student played:</label>
                              <div className="move-chips" style={{ marginTop: 4 }}>
                                {(attempt?.moves || []).map((m, i) => (
                                  <span key={i} className={`move-chip ${i < reviewNavIdx ? "done-chip" : ""}`}
                                    onClick={(e) => { e.stopPropagation(); setReviewNavIdx(i + 1); }}
                                    style={{ cursor: "pointer" }}>{m.san}</span>
                                ))}
                                {!attempt && <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Not attempted</span>}
                              </div>
                            </div>
                            <div>
                              <label>Correct answer:</label>
                              <div className="move-chips" style={{ marginTop: 4 }}>
                                {puzzle.solution.map((m, i) => <span key={i} className="move-chip correct-chip">{m.san}</span>)}
                              </div>
                            </div>
                            <div className="board-nav" style={{ marginTop: 12, justifyContent: "flex-start" }}>
                              <span style={{ fontSize: 11, color: "var(--text-muted)", marginRight: 4 }}>Moves:</span>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewNavIdx(0); }} disabled={reviewNavIdx === 0}>⟪</button>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewNavIdx(i => Math.max(0, i-1)); }} disabled={reviewNavIdx === 0}>‹</button>
                              <span className="nav-info">{reviewNavIdx + 1}/{fens.length}</span>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewNavIdx(i => Math.min(fens.length-1, i+1)); }} disabled={reviewNavIdx >= fens.length-1}>›</button>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewNavIdx(fens.length-1); }} disabled={reviewNavIdx >= fens.length-1}>⟫</button>
                            </div>
                            <div className="board-nav" style={{ marginTop: 8, justifyContent: "flex-start" }}>
                              <span style={{ fontSize: 11, color: "var(--text-muted)", marginRight: 4 }}>Puzzle:</span>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewSelPuzzle(i => Math.max(0, i-1)); }} disabled={reviewSelPuzzle === 0}>‹</button>
                              <span className="nav-info">{reviewSelPuzzle + 1}/{viewSet.puzzles.length}</span>
                              <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setReviewSelPuzzle(i => Math.min(viewSet.puzzles.length-1, i+1)); }} disabled={reviewSelPuzzle >= viewSet.puzzles.length-1}>›</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
